import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import {
	fetchUsers,
	resetPassword,
	fetchUserById,
	fetchUserSetting,
	fetchUserDocuments,
	updateUser,
} from '../slices/userSlice';
import { ChevronDown, ChevronRight, X, Edit2, Download } from 'react-feather';
import { useForm } from 'react-hook-form';
import user_placeholder from '../assets/user_placeholder.png';
import { useNavigate } from 'react-router-dom';
import { formatPhoneNumberInput } from '../hooks/phoneFormat';

export default function CurrentUserProfile() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const api = process.env.REACT_APP_API_URL;

	// --- State and Selectors ---
	const [editForm, setEditForm] = useState(false);
	const [sections, setSections] = useState({ generalInfo: true });

	// Get the core user object from the Redux store
	const currentUser = useSelector((state) => state.auth.self);
	const userSettings = useSelector((state) => state.auth.userSetting);
	const userDocuments = useSelector((state) => state.auth.userDocuments?.items); // Optional chaining for safety

	const {
		register,
		handleSubmit,
		setValue,
		formState: { errors },
	} = useForm();

	// --- Data Fetching Logic ---

	// Fetch dependent data once `currentUser` is available.
	useEffect(() => {
		// This is a "guard clause" that prevents running with an undefined ID.
		if (currentUser && currentUser.id) {
			// EMPLOYEE role should only see personal info (avoid extra admin-only calls)
			if (currentUser.role !== 'EMPLOYEE') {
				dispatch(fetchUserSetting(currentUser.id));
				dispatch(fetchUserDocuments({ userId: currentUser.id }));
			}
		}
	}, [dispatch, currentUser]); // This effect re-runs when `currentUser` changes.

	// This function toggles sections open/closed
	const toggleSection = (section) => {
		setSections((prev) => ({ ...prev, [section]: !prev[section] }));
	};

	// --- Form Submission Handlers ---

	const onSubmitUpdatePassword = (data) => {
		if (currentUser?.email && data.password) {
			dispatch(
				resetPassword({ email: currentUser.email, password: data.password })
			);
			setEditForm(false);
		}
	};

	const onSubmitUpdateUser = (data) => {
		const { firstName, lastName, phoneNumber } = data;
		if (currentUser?.id) {
			dispatch(
				updateUser({
					id: currentUser.id,
					firstName,
					lastName,
					phoneNumber, // Assuming formatting is handled elsewhere or not needed here
					profilePicture: '', // You need to get the URL from your file upload state
				})
			);
			setEditForm(false);
		}
	};

	// If there is no user data yet, show a loading message.
	// This prevents errors from trying to read properties of `null`.
	if (!currentUser) {
		return <div>Loading profile...</div>;
	}

	return (
		<div className="profilepage">
			<aside className="details-panel">
				<div className="details-number">
					<h1>My Account</h1>
					<div className="block-right">
						<button
							onClick={() => setEditForm(!editForm)}
							className="btn-edit-item"
						>
							{editForm ? (
								<>
									<X size={24} /> Cancel
								</>
							) : (
								<>
									Edit Details <Edit2 size={20} />
								</>
							)}
						</button>
					</div>
				</div>

				{editForm ? (
					<>
						{/* --- EDIT MODE JSX --- */}
						{/* General Info Form */}
						<form onSubmit={handleSubmit(onSubmitUpdateUser)}>
							{/* ... your input fields for firstName, lastName, phoneNumber ... */}
							<button type="submit" className="redBtn">
								Update General Information
							</button>
						</form>

						{/* Password Form */}
						<form onSubmit={handleSubmit(onSubmitUpdatePassword)}>
							{/* ... your password input field ... */}
							<button type="submit" className="redBtn">
								Update Password
							</button>
						</form>
					</>
				) : (
					<>
						{/* --- VIEW MODE JSX --- */}
						<div className="user-information">
							<div className="user-information-block">
								<div
									className="title-block"
									onClick={() => toggleSection('generalInfo')}
								>
									<h2 className="text-xl font-bold mb-4">
										General Information
									</h2>
									<button>
										{sections.generalInfo ? (
											<ChevronDown size={24} />
										) : (
											<ChevronRight size={24} />
										)}
									</button>
								</div>
								{sections.generalInfo && (
									<div className="content-block">
										<p>
											<strong>First Name:</strong> {currentUser.firstName}
										</p>
										<p>
											<strong>Last Name:</strong> {currentUser.lastName}
										</p>
										<p>
											<strong>Email:</strong> {currentUser.email}
										</p>
										<p>
											<strong>Role:</strong>{' '}
											{currentUser.role || 'No role assigned'}
										</p>
									</div>
								)}
							</div>
						</div>
						{/* ... other sections to display userSettings, userDocuments etc. ... */}
					</>
				)}
			</aside>
		</div>
	);
}
